import { Topbar, Aside, Main, ListLoans, Loader } from 'components';
import { PlusIcon } from "@heroicons/react/24/solid";
import { HugeiconsIcon } from '@hugeicons/react';
import { Alert01Icon } from '@hugeicons/core-free-icons';
import { Link } from 'react-router-dom';
import { useUser, useLoans } from 'hooks';
import { validateGhanaCard } from 'components/misc/functions';
import { numberFormatter } from 'components/misc/constants';

const activeStages = ['active', 'pendingDisbursement', 'pendingFacilityLetter'];

function Index() {
    const { user } = useUser();
    const { isLoading, loans } = useLoans();

    if (isLoading) {
        return <Loader />
    }

    const activeLoans = loans?.filter(l => activeStages.includes(l?.approvalStage)) ?? [];
    const totalBorrowed = loans?.filter(l => l?.approvalStage === 'active' || l?.approvalStage === 'completed')
        .reduce((sum, l) => sum + (Number(l?.amount) || 0), 0) ?? 0;

    return (
        <main>
            <Topbar />

            <div className='pt-14 w-full flex overflow-hidden bg-light-bg dark:bg-dark-page'>
                <Aside />

                <Main>
                    {(user?.isVerified && validateGhanaCard(user?.idNumber)) ? (
                        <div className="space-y-6 py-4">

                            {/* Page header */}
                            <div className="flex items-start justify-between">
                                <div>
                                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                                        Welcome back, {user?.otherNames}
                                    </h1>
                                    <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                                        Here's a summary of your loan activity
                                    </p>
                                </div>

                                <Link
                                    to="/loans"
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-gold-600 hover:bg-gold-700 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors"
                                >
                                    <PlusIcon className="h-4 w-4" />
                                    Apply Now
                                </Link>
                            </div>

                            {/* Stats row */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="rounded-xl border border-gray-200 dark:border-white/[.08] bg-white dark:bg-gray-900 px-5 py-4 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Total Applications</p>
                                    <p className="mt-1.5 text-2xl font-semibold text-gray-900 dark:text-white tabular-nums">{loans?.length ?? 0}</p>
                                </div>
                                <div className="rounded-xl border border-gray-200 dark:border-white/[.08] bg-white dark:bg-gray-900 px-5 py-4 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Active Loans</p>
                                    <p className="mt-1.5 text-2xl font-semibold text-gray-900 dark:text-white tabular-nums">{activeLoans.length}</p>
                                </div>
                                <div className="rounded-xl border border-gray-200 dark:border-white/[.08] bg-white dark:bg-gray-900 px-5 py-4 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Total Borrowed</p>
                                    <p className="mt-1.5 text-2xl font-semibold text-gray-900 dark:text-white tabular-nums">GHS {numberFormatter(totalBorrowed)}</p>
                                </div>
                            </div>

                            {/* Loans table */}
                            <div>
                                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">All Applications</h2>
                                <ListLoans loans={loans} />
                            </div>
                        </div>
                    ) : (
                        <div className="py-4">
                            <div className="rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/30 px-4 py-3.5 flex items-start gap-3">
                                <HugeiconsIcon icon={Alert01Icon} size={18} className="text-amber-500 shrink-0 mt-0.5" />
                                <p className="text-sm text-amber-700 dark:text-amber-400">
                                    {user?.verificationStatus === 'pending' ? (
                                        <>
                                            <strong className="font-semibold">Verification in progress.</strong>{' '}
                                            Our team is reviewing your <strong>GhanaCard</strong> details. Loan applications open once your account is verified.
                                        </>
                                    ) : (
                                        <>
                                            <strong className="font-semibold">Account not verified.</strong>{' '}
                                            Complete your <strong>Next of Kin</strong> and <strong>GhanaCard</strong> details to unlock loan applications.{' '}
                                            <Link to="/profile/edit" className="underline font-semibold hover:text-amber-900 dark:hover:text-amber-300">Complete profile →</Link>
                                        </>
                                    )}
                                </p>
                            </div>
                        </div>
                    )}
                </Main>
            </div>
        </main>
    )
}

export default Index
